import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Lightweight, zero-dependency i18n layer for TMC Studio.
 *
 * The public API intentionally mirrors react-i18next (`useTranslation().t`,
 * `i18n.changeLanguage`) so the engine can later be swapped for react-i18next
 * — for the macOS / Android / desktop builds — without touching call sites.
 * Translation dictionaries live in ./locales and are plain JSON-shaped objects,
 * so they are reusable across web, React Native and Electron/Tauri.
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { en } from './locales/en.js';
import { pl } from './locales/pl.js';
import { es } from './locales/es.js';
export const LANGUAGES = ['pl', 'en', 'es'];
const dictionaries = { en, pl, es };
const STORAGE_KEY = 'tmc-language';
function detectInitial() {
    if (typeof window !== 'undefined') {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY);
            if (saved && LANGUAGES.includes(saved))
                return saved;
        }
        catch {
            /* ignore storage errors */
        }
        const nav = (navigator?.language || 'en').slice(0, 2).toLowerCase();
        if (nav === 'pl' || nav === 'es')
            return nav;
    }
    return 'en';
}
function resolve(dict, key) {
    return key.split('.').reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), dict);
}
function interpolate(str, vars) {
    if (!vars)
        return str;
    return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{{${k}}}`));
}
function makeT(language) {
    return (key, vars) => {
        const val = resolve(dictionaries[language], key) ?? resolve(dictionaries.en, key) ?? key;
        return typeof val === 'string' ? interpolate(val, vars) : key;
    };
}
/** Non-React translation helper for stores/services that cannot use hooks. */
export function translate(key, vars, language) {
    return makeT(language ?? detectInitial())(key, vars);
}
const I18nContext = createContext(null);
export const LanguageProvider = ({ children, initialLanguage, onLanguageChange }) => {
    const [language, setLanguageState] = useState(() => initialLanguage ?? detectInitial());
    useEffect(() => {
        if (typeof document !== 'undefined')
            document.documentElement.lang = language;
    }, [language]);
    const setLanguage = useCallback((lang) => {
        setLanguageState(lang);
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(STORAGE_KEY, lang);
            }
            catch {
                /* ignore */
            }
            onLanguageChange?.(lang);
        }
    }, [onLanguageChange]);
    const t = useCallback((key, vars) => makeT(language)(key, vars), [language]);
    return (_jsx(I18nContext.Provider, { value: { language, setLanguage, t }, children: children }));
};
/** Mirrors react-i18next's useTranslation(); safe to use without a provider. */
export function useTranslation() {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        const t = makeT('en');
        return {
            t,
            language: 'en',
            setLanguage: (_lang) => { },
            i18n: { language: 'en', changeLanguage: (_lang) => { } },
        };
    }
    return {
        t: ctx.t,
        language: ctx.language,
        setLanguage: ctx.setLanguage,
        i18n: { language: ctx.language, changeLanguage: ctx.setLanguage },
    };
}
//# sourceMappingURL=i18n.js.map